import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { selectUserProjectId } from 'core/userContext/selectors';
import { WhatsAppContentDefinition } from 'modules/graphql/types';
import { getDefaultLinkShorteningDomain } from 'modules/mailDomains/selectors';
import { TemplateAndContentFormDataLocale } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useShortLinksLength } from 'modules/templateEditor/hooks/useShortLinksLength';
import { DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN } from 'modules/templateEditor/EditSMS/constants';

import {
  WhatsAppLinkShorteningContext,
  WhatsAppLinkShorteningContextState,
} from './WhatsAppLinkShorteningContext';

export const getUseWhatsAppShorteningKey = (projectId: number | string) => {
  return `useWhatsAppLinkShortening-proj-${projectId}`;
};

export const getWhatsAppLinkShorteningDomainKey = (projectId: number | string) => {
  return `WhatsAppLinkShorteningDomain-proj-${projectId}`;
};

export const WhatsAppLinkShorteningContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { initialTemplate, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const whatsAppData = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const initialWhatsAppData = (initialTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const initialData = (initialTemplate || {}) as TemplateAndContentFormDataLocale;
  const isNewTemplate = !initialData.name?.length;
  const projectId = useSelector(selectUserProjectId);
  const defaultLinkShorteningDomain = useSelector(getDefaultLinkShorteningDomain);

  const [isLinkShorteningLocallyEnabled, setIsLinkShorteningLocallyEnabled] =
    useLocalStorage<boolean>(getUseWhatsAppShorteningKey(projectId), false);
  const [localStorageDomain, setLocalStorageDomain] = useLocalStorage<string>(
    getWhatsAppLinkShorteningDomainKey(projectId),
    '',
  );
  /** Determines the status of the checkbox for shortening and tracking links in WhatsApp
   * Local storage will be implemented in future PR
   */
  const [isWhatsAppLinkShorteningEnabled, setIsWhatsAppLinkShorteningEnabled] = useState<boolean>(
    !!whatsAppData.trackingDomain || !!(isNewTemplate && isLinkShorteningLocallyEnabled),
  );

  // default link shortening domain
  const defaultWhatsAppLinkShorteningDomain =
    defaultLinkShorteningDomain ?? DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN;

  /** To sync Shorten and track data from local storage, we need to update both */
  const updateIsWhatsAppLinkShorteningEnabledState = (value: boolean) => {
    if (!value) {
      setCurrentMediumField('linkParams', null);
    }
    setIsWhatsAppLinkShorteningEnabled(value);
    setIsLinkShorteningLocallyEnabled(value);
  };

  const handleSetIsWhatsAppLinkShorteningEnabled = () => {
    const newValue = !isWhatsAppLinkShorteningEnabled;
    updateIsWhatsAppLinkShorteningEnabledState(newValue);
    const { trackingDomain } = whatsAppData;
    if (!newValue) {
      setCurrentMediumField('trackingDomain', null);
    } else if (!trackingDomain) {
      setCurrentMediumField('trackingDomain', defaultWhatsAppLinkShorteningDomain);
      setLocalStorageDomain(defaultWhatsAppLinkShorteningDomain);
    }
  };

  const handleSetLocalStorageDomain = (newDomains: string[]) => {
    setCurrentMediumField('trackingDomain', newDomains[0]);
    setLocalStorageDomain(newDomains[0] as string);
  };

  const handleResetIsWhatsAppLinkShorteningEnabled = () => {
    updateIsWhatsAppLinkShorteningEnabledState(
      !!initialWhatsAppData.trackingDomain || !!(isNewTemplate && isLinkShorteningLocallyEnabled),
    );
  };

  // Get the shortlink length
  const shortlinkLength = useShortLinksLength(initialTemplate);

  const context: WhatsAppLinkShorteningContextState = {
    isWhatsAppLinkShorteningEnabled,
    localStorageDomain,
    setIsWhatsAppLinkShorteningEnabled: handleSetIsWhatsAppLinkShorteningEnabled,
    shortlinkLength,
    resetIsWhatsAppLinkShorteningEnabled: handleResetIsWhatsAppLinkShorteningEnabled,
    setLocalStorageDomain: handleSetLocalStorageDomain,
    defaultWhatsAppLinkShorteningDomain,
  };

  return (
    <WhatsAppLinkShorteningContext.Provider value={context}>
      {children}
    </WhatsAppLinkShorteningContext.Provider>
  );
};
